import { ThemeToggle } from '@/components/ThemeToggle';
import { useAuth } from '@/hooks/useAuth';
import { getUserShortName } from '@/lib/user';
import { ArrowLeft, Search, Store, User } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { NotificationBell } from './notifications/NotificationBell';
import { InstallAppButton } from './pwa/InstallAppButton';

interface HeaderProps {
  showBack?: boolean;
  title?: string;
  showSearch?: boolean;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
}

const Header = ({ showBack, title, showSearch = false, searchValue, onSearchChange }: HeaderProps) => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user } = useAuth();
  const accountHref = isAuthenticated ? '/perfil' : '/entrar';
  const accountLabel = isAuthenticated ? getUserShortName(user) : 'Entrar';

  return (
    <header className="border-border bg-background/95 sticky top-0 z-40 border-b backdrop-blur-sm">
      <div className="container flex min-h-16 items-center justify-between gap-1 py-2 sm:gap-2 md:gap-3">
        {showBack ? (
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="hover:bg-muted -ml-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg transition-colors"
            aria-label="Voltar"
          >
            <ArrowLeft className="text-foreground h-5 w-5" />
          </button>
        ) : null}

        {!title && (
          <Link to="/" className="flex min-w-0 flex-wrap items-center justify-end gap-1 sm:gap-2">
            <div className="bg-primary flex h-8 w-8 items-center justify-center rounded-lg">
              <span className="text-primary-foreground text-sm font-bold">VT</span>
            </div>
            <span className="text-foreground hidden text-lg font-bold sm:block">Vira Tempo</span>
          </Link>
        )}

        {title && <h1 className="text-foreground min-w-0 flex-1 text-center text-base font-semibold">{title}</h1>}

        {showSearch && (
          <div className="mx-2 hidden max-w-md min-w-0 flex-1 md:block">
            <div className="relative">
              <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
              <input
                type="text"
                aria-label="Buscar estabelecimento"
                placeholder="Buscar estabelecimento"
                value={searchValue || ''}
                onChange={(e) => onSearchChange?.(e.target.value)}
                className="bg-muted text-foreground placeholder:text-muted-foreground focus:ring-primary/30 h-11 w-full rounded-xl border-none pr-4 pl-9 text-sm transition-[color,background-color,border-color,box-shadow,opacity,transform,translate] outline-none focus:ring-2"
              />
            </div>
          </div>
        )}

        {!title && <div className="flex-1" />}

        <div className="flex min-w-0 flex-wrap items-center justify-end gap-1 sm:gap-2">
          <ThemeToggle />
          {!title && (
            <>
              <a
                href="https://admin.viratempo.app"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground hidden h-11 items-center gap-1.5 rounded-xl px-4 text-sm font-medium transition-colors xl:flex"
              >
                <Store className="h-4 w-4" />
                Cadastrar meu negócio
              </a>
              {isLoading ? (
                <div className="bg-muted h-10 w-10 rounded-xl sm:w-28" aria-hidden="true" />
              ) : (
                <>
                  <InstallAppButton />
                  <NotificationBell />
                  <Link
                    to={accountHref}
                    aria-label={isAuthenticated ? `Abrir perfil de ${accountLabel}` : 'Entrar na sua conta'}
                    className="bg-muted text-foreground hover:bg-muted/80 flex h-11 min-w-11 items-center justify-center gap-2 rounded-xl px-3 text-sm font-medium transition-colors sm:px-4"
                  >
                    <User className="h-4 w-4 shrink-0" />
                    <span
                      className={
                        isAuthenticated ? 'hidden max-w-24 truncate sm:inline sm:max-w-28' : 'hidden sm:inline'
                      }
                    >
                      {accountLabel}
                    </span>
                  </Link>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
